import { calculatePartialDateDiffenceDuration, isPartialDate, nowPartialDate, sortPartialDate } from 'neuro-utils';
import { parkinsonDiagnoses } from 'Routes/Diagnosis/utils/definitions';
import { parkinsonInfusionMedicationUnfilledDose } from 'Routes/Medication/utils/parkinsonUnfilledFields';
import { filterByDocumentType } from 'Utility/documentHandling';
import { defineDocConfig } from './utils';

import type { ClinicalArchive } from '.';

export const parkinsonDocumentTypes = defineDocConfig([
  {
    name: 'diagnosis',
    subTypes: ['diagnosis', 'parkinsonSymptoms', 'pd', 'msa', 'psp', 'dlb', 'cbd', 'ftd', 'ppa', 'alzheimer'],
    secondaryDocs: ['cdr', 'examination'],
  },
  {
    name: 'diagnosticSurvey',
    subTypes: ['npi', 'fbimod', 'bnsq', 'adl', 'iadl'],
    secondaryDocs: ['diagnosis', 'mmse'],
  },
  { name: 'medication', subTypes: ['medication', 'adverseEffect'] },
  { name: 'dbs' },
  { name: 'thalamotomyHifu', subTypes: ['thalamotomy', 'hifu'] },
  {
    name: 'updrs',
    subTypes: ['updrs_iii', 'updrs_v', 'ftmtrs', 'levodopatest', 'dbsEffect'],
    secondaryDocs: ['dbs'],
  },
  { name: 'cognition', subTypes: ['moca', 'mmse', 'tmt', 'cdr', 'sdmt', 'cerad'], secondaryDocs: ['background'] },
  { name: 'imaging', subTypes: ['mri', 'tt', 'scintigraphy'] },
  { name: 'ariaSymptoms' },
  { name: 'geneTest' },
  { name: 'examination' },
  { name: 'background', subTypes: ['background', 'measurement'] },
  { name: 'lifestyle' },
  { name: 'myService', title: 'titleShort' },
]);

export const parkinsonGraphDocTypes = [
  'diagnosis',
  'geneTest',
  'thalamotomy',
  'hifu',
  'updrs_v',
  'updrs_iii',
  'ftmtrs',
  'medication',
  'adverseEffect',
  'moca',
  'mmse',
  'tmt',
  'cdr',
  'dbsEffect',
  'dbs',
  'levodopatest',
  'sdmt',
  'mri',
  'tt',
  'scintigraphy',
  'fbimod',
  'examination',
  'adl',
  'iadl',
  'cerad',
  'ariaSymptoms',
] satisfies TDocumentType[];

// Filter these routes/types from showing in page navigations based on these rules
export const parkinsonFilteredTypes = (docs: Array<IDocumentV2>): string[] => {
  let parkDiagWithDbs: IDiagnosis | undefined = undefined;
  let dbsDocs: Array<IDocumentWithTypeV2<'dbs'>> = [];
  let parkDiagWithDbsIndex = -1;
  let alzheimerDiag = false;
  let thalamotomyDocs: Array<IDocumentWithTypeV2<'thalamotomy'>> = [];
  let hifuDocs: Array<IDocumentWithTypeV2<'hifu'>> = [];

  const parkinsonDiags = new Set([
    'G20',
    'G23.1',
    'G90.3',
    'G23.2',
    'G23.3',
    'G31.0',
    'G25.9',
    'F02.8*G31.8',
    'F00.2*G30.8',
    'F02.0*G31.0',
    'G25',
    'G25.0',
    'G25.1#',
    'G25.2',
    'G25.3#',
    'G25.4#',
    'G25.5',
    'G25.6#',
    'G25.8',
  ]);
  const diagnosesToShowDBS = new Set(['G20', 'G25.0']);
  const alzheimerDiags = new Set(['G30', 'G30.0', 'G30.1', 'G30.8', 'G30.9', 'R41.8']);

  if (docs.length > 0) {
    const diagnosisDocs = filterByDocumentType(docs, 'diagnosis');
    const parkinsonDiagDocs = diagnosisDocs.filter(
      doc => doc.diagnosis && typeof doc.diagnosis === 'string' && parkinsonDiags.has(doc.diagnosis),
    );
    parkDiagWithDbs = parkinsonDiagDocs.find(
      d => d.diagnosis && typeof d.diagnosis === 'string' && diagnosesToShowDBS.has(d.diagnosis),
    );
    parkDiagWithDbsIndex = parkinsonDiagDocs
      .toSorted((d1, d2) => {
        if ('date' in d1 && 'date' in d2 && isPartialDate(d1.date) && isPartialDate(d2.date)) {
          return sortPartialDate(d2.date, d1.date);
        }
        return 0;
      })
      .findIndex(d => d.diagnosis && typeof d.diagnosis === 'string' && diagnosesToShowDBS.has(d.diagnosis));

    dbsDocs = filterByDocumentType(docs, 'dbs');
    thalamotomyDocs = filterByDocumentType(docs, 'thalamotomy');
    hifuDocs = filterByDocumentType(docs, 'hifu');

    alzheimerDiag = !!diagnosisDocs.find(
      diag => diag.diagnosis && typeof diag.diagnosis === 'string' && alzheimerDiags.has(diag.diagnosis),
    );
  }

  const filteredSections = [];
  if (!alzheimerDiag) {
    filteredSections.push('ariaSymptoms');
  }
  // Include DBS and thalamotomyHifu if ( G20 or G25.0 diagnosis is latest movementDisorderDiagnosis AND no alzheimer diagnosis found)
  if (!(parkDiagWithDbs && parkDiagWithDbsIndex === 0 && !alzheimerDiag)) {
    // If dbs docs found, do not filter the tile
    if (dbsDocs.length === 0) {
      filteredSections.push('dbs');
    }
    // If thalamotomy or hifu docs found, do not filter the tile
    if (thalamotomyDocs.length === 0 && hifuDocs.length === 0) {
      filteredSections.push('thalamotomyHifu');
    }
  }
  return filteredSections;
};

export const parkinsonAlerts: IAlert[] = [
  {
    // Notification to the user if the estimated time to replace the DBS generator is less than one year.
    content: 'alerts.parkinson.dbsOneYear',
    id: 'parkinsonDbsOneYear',
    severity: 'warning',
    popup: true,
    rule: (docs: IDocumentV2[] | undefined = []): boolean => {
      // Find first dbs document (docs should be sorted descending by commitDate)
      const dbsDoc = filterByDocumentType(docs, 'dbs').at(0);
      const generatorChangeDate = dbsDoc?.generators?.[0]?.generatorNextChangeDate;
      if (!generatorChangeDate) return false;
      const duration = calculatePartialDateDiffenceDuration('days', generatorChangeDate);
      return dbsDoc.status !== 'ended' && Math.abs(duration.days) < 365;
    },
  },
  {
    // Notification if infusion medication documents from integration has unfilled regimen doses or dose times
    content: 'alerts.parkinson.infusionMedicationUnfilledDose',
    id: 'parkinsonInfusionMedicationUnfilledDose',
    severity: 'info',
    popup: true,
    rule: parkinsonInfusionMedicationUnfilledDose,
  },
  {
    // Notification to the user if a diagnosis document is recorded before a parkinsonSymptoms document
    content: 'alerts.parkinson.symptomOnsetDateAfterDiagnosisDate',
    id: 'parkinsonSymptomOnsetDateAfterDiagnosisDate',
    severity: 'warning',
    popup: true,
    rule: (docs = []) => {
      // Find first Parkinson diagnosis document (docs should be sorted descending by commitDate)
      const parkinsonDiagnosisDoc = filterByDocumentType(docs, 'diagnosis').find(
        d => 'diagnosis' in d && (parkinsonDiagnoses as string[]).includes(d.diagnosis ?? ''),
      );
      const parkinsonDiagnosisDate = parkinsonDiagnosisDoc?.date;

      // Find first Parkinson diagnosis document (docs should be sorted descending by commitDate)
      const parkinsonSymptomsDoc = filterByDocumentType(docs, 'parkinsonSymptoms').at(0);
      const parkinsonSymptomOnsetDate = parkinsonSymptomsDoc?.symptomOnsetDate;

      if (!parkinsonDiagnosisDate || !parkinsonSymptomOnsetDate) return false;
      return sortPartialDate(parkinsonSymptomOnsetDate, parkinsonDiagnosisDate) > 0;
    },
  },
  {
    // Notification if MRI has been filled with ARIA findings to remind filling ARIA symptoms document
    content: 'alerts.parkinson.alzheimerAriaSymptomsReminder',
    id: 'alzheimerMRIAriaFindingsReminder',
    severity: 'info',
    popup: true,
    rule: (docs = []) => {
      const ariaSymptomDocs = filterByDocumentType(docs, 'ariaSymptoms');

      // 1. Get all medications related to ARIA (e.g. Lecanemab)
      const medicationDocsRelatedToAria = filterByDocumentType(docs, 'medication').filter(d =>
        medicationATCsForAria.includes(d.atc ?? ''),
      );

      // If no ARIA-related medication exists, do not show the reminder
      if (medicationDocsRelatedToAria.length === 0) {
        return false;
      }

      // 2. Find the earliest start date of the relevant medication
      const earliestMedicationDate = medicationDocsRelatedToAria
        .map(m => m.startDate)
        .toSorted((a, b) => sortPartialDate(a, b))[0];

      // 3. Filter for MRI documents that have ARIA findings AND occurred AFTER the medication started
      const mriDocsWithAriaAfterMedication = filterByDocumentType(docs, 'mri').filter(d => {
        if (d._editing) return false;
        const mri = d;

        const hasAriaFindings = !!((mri.ariaE?.finding && mri.ariaE.finding !== 'no') ?? mri.ariaH?.finding === 'yes');

        // Check if the MRI date is the same as or later than the medication start date
        const isAfterOrOnMedicationStart = sortPartialDate(mri.date, earliestMedicationDate) >= 0;

        return hasAriaFindings && isAfterOrOnMedicationStart;
      });

      // If no relevant MRI findings exist post-medication start, do not show the reminder
      if (mriDocsWithAriaAfterMedication.length === 0) {
        return false;
      }

      // 4. Get the latest MRI with findings and the latest ARIA symptoms document
      const latestMriWithAria = mriDocsWithAriaAfterMedication.toSorted((m1, m2) =>
        sortPartialDate(m2.date, m1.date),
      )[0];

      const latestAriaSymptoms = ariaSymptomDocs.toSorted((a1, a2) => sortPartialDate(a2.date, a1.date)).at(0);

      // 5. Trigger reminder only if the latest post-medication MRI is newer than the latest symptom documentation
      return sortPartialDate(latestMriWithAria?.date, latestAriaSymptoms?.date) === 1;
    },
    fillNewButton: { route: 'ariaSymptoms', documentType: 'ariaSymptoms', initData: { date: nowPartialDate() } },
  },
];

export const medicationATCsForAria = ['N06DX04', 'N06DX05']; // lekanemabi, donanemabi

export const parkinsonClinicalArchiveTypes: readonly ClinicalArchive[] = ['NEU', 'HOI'] as const;
